import { DoArraysOverlap } from "../../../../shared/scripts/array-functions";


export const STUDY_INDEPENDENT_ID = -1;
// Study-independent trainings have no real study, and the portal-wide user view has no
// site, so both params are nullable throughout the LMS settings pages. These build the
// query-string suffix, omitting the param entirely when there's no value rather than
// serializing the literal "null" (which would model-bind to 0 on an int).
export function siteIdParam(siteId: number | null): string {
    return siteId != null ? `&siteId=${siteId}` : '';
}

export function studyIdParam(studyId: number | null): string {
    return studyId != null ? `&studyId=${studyId}` : '';
}

export class LMSRoleDto {
    roleId: number;
    name: string;
}

export class LMSStudyDto {
    studyId: number;
    name: string;
}

export class LMSTrainingOverviewDto {
    trainingId: number;
    trainingName: string;
    roles: LMSRoleDto[];
    isStudyIndependent: boolean;
    studies: LMSStudyDto[];
    timeToComplete: string;
    expirationTime: string;
    isArchived: boolean;

    matchesFilter(selectedStudyIds: string[], selectedDependent: string[], selectedRoleIds: string[]): boolean {
        if (selectedStudyIds?.length > 0) {
            const studyIds = this.studies.map(study => study.studyId.toString());
            if (!DoArraysOverlap(selectedStudyIds, studyIds)) return false;
        }
        
        if (selectedDependent?.length > 0) {
            const isDependent = !this.isStudyIndependent ? "yes" : "no";
            if (!selectedDependent.includes(isDependent)) return false;
        }
        
        if (selectedRoleIds?.length > 0) {
            const roleIds = this.roles.map(role => role.roleId.toString());
            if (!DoArraysOverlap(selectedRoleIds, roleIds)) return false;
        }
        
        return true;
    }
}

export class LMSTrainingMaterialDto {
    id: number;
    fileName: string;
    materialLink: string;
}

export class LMSTrainingDetailDto {
    Id: number;
    Title: string;
    Instructions: string;
    DefaultCompletionWindowInDays: number;
    ExpectedCompletionTime: string;
    CanExpire: boolean;
    DefaultExpirationTimeInDays: number;
    RequiresUpload: boolean;
    IsStudyIndependent: boolean;
    Notes: string;
    IsArchived: boolean;
    RoleIds: number[];
    StudyIds: number[];
    Materials: LMSTrainingMaterialDto[];
}

export class Counts {
    overdue: number;
    incomplete: number;
    expired: number;
    complete: number;
    total: number;
}
export class LMSSiteStudyOverviewDto extends Counts {
    siteId: number;
    siteName: string;
    studyId: number | null;
    studyName: string;

    matchesFilter(selectedStudyIds: string[]): boolean {
        if (selectedStudyIds.length > 0) {
            const idToMatch = this.studyId != null ? this.studyId.toString() : STUDY_INDEPENDENT_ID.toString();
            return selectedStudyIds.includes(idToMatch);
        }

        return true;
    }
}

export class LMSSiteStudyTrainingDto extends Counts {
    trainingId: number;
    trainingName: string;
}

export class LMSSiteStudyUserDto extends Counts {
    userId: number;
    userName: string;
    roles: string;
}

export class LMSSiteStudyTrainingUserDto {
    userTrainingId: number;
    userId: number;
    userName: string;
    roles: string;
    completedAt: string;
    expirationDate: string;
    status: string;
}

export type LMSTrainingStatus = "pending" | "overdue" | "complete" | "expired";

export class LMSSiteStudyUserTrainingDto {
    userTrainingId: number;
    trainingId: number;
    trainingName: string;
    daysRemaining: number;
    expirationDate: string;
    completedAt: string;
    isComplete: boolean;

    getStatus(): LMSTrainingStatus {
        if (this.isComplete) {
            return this.hasExpired() ? "expired" : "complete";
        }

        return this.daysRemaining < 0 ? "overdue" : "pending";
    }

    private hasExpired(): boolean {
        if (!this.expirationDate) {
            return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return new Date(this.expirationDate) < today;
    }
}

export class LMSUserTrainingDetailDto {
    userTrainingId: number;
    trainingId: number;
    trainingName: string;
    notes: string;
    dueDate: string;
    expirationDate: string;
    isComplete: boolean;
    requiresUpload: boolean;
    canExpire: boolean;
    defaultExpirationTimeInDays: number;
    uploads: LMSTrainingMaterialDto[];
}

export class LMSUserTrainingAssignmentStateDto {
    trainingId: number;
    userTrainingId: number | null;
    isActive: boolean;
    hasAdminOverride: boolean;
}